/** @jsxRuntime automatic */
import { forwardRef } from "react";
import { Button, type ButtonProps } from "./button";
import type { SwapEmphasis, SwapKey } from "./swap";
import { LazySwap } from "./swap-boot";
import { TooltipBubble, type TooltipAlign, type TooltipSide } from "./tooltip";
import { cn } from "./utils";

export interface IconButtonProps extends Omit<ButtonProps, "size" | "children" | "aria-label" | "title"> {
  label: string;
  children: ButtonProps["children"];
  /**
   * `icon` 44 px (default) · `icon-sm` 32 px with a mouse in the wide layout,
   * 44 px with a finger · `icon-xs` 28 px inside a row, 44 px to the finger.
   */
  size?: "icon" | "icon-sm" | "icon-xs";
  /**
   * The label as a tooltip on hover and keyboard focus (never a native
   * `title`, never stuck after a tap). `false` when the icon is already
   * labeled next to it. Default: above, centered.
   */
  tooltip?: boolean | { side?: TooltipSide; align?: TooltipAlign; keys?: readonly string[] };
  /**
   * What the glyph says (saved or not, Send or Stop): a new key replaces the
   * glyph in place (`Swap variant="icon"`, after the mount only). A glyph
   * that only turns (Plus → ×, `rotate-45`) needs none.
   */
  swapKey?: SwapKey;
  /**
   * With `swapKey`, whose state the glyph says (an offer's id once its saved
   * state is known, never before): when it changes, the new glyph lands at
   * once. So a row's bookmark never pops because its data arrived after the
   * first paint, nor on J/K.
   */
  subjectKey?: SwapKey;
  /**
   * `pop` when the new glyph is affirmative (saved, done): it peaks at 1.08
   * and its 15 % fill floods in. Default: `pop` when the button turns
   * pressed (`aria-pressed`), the quiet replace otherwise.
   */
  emphasis?: SwapEmphasis;
}

/**
 * A button with only an icon: its `label` is the accessible name and the
 * tooltip. Press (`press-glyph`, spec 07 › Foco e interacción): the glyph
 * presses to .88 and the box stays at 1; the ghost tints at 0 ms. With
 * `swapKey` the glyph lives in a Swap slot (the one the press scales) and the
 * bubble comes first, so a leaving glyph never stands before it. In a burst
 * (hot mode: S, S, S) the glyph keeps the quiet replace and never pops
 * (`data-hot="quiet"`, globals.css).
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, className, size = "icon", tooltip = true, swapKey, subjectKey, emphasis, children, ...props }, ref) => {
    const bubble = tooltip === false ? null : tooltip === true ? {} : tooltip;
    const tip = bubble ? (
      <TooltipBubble side={bubble.side} align={bubble.align} keys={bubble.keys}>
        {label}
      </TooltipBubble>
    ) : null;
    return (
      <Button
        ref={ref}
        size={size}
        aria-label={label}
        className={cn("press-glyph motion-safe:active:scale-100", bubble && "group/tooltip", className)}
        {...props}
      >
        {swapKey === undefined ? (
          <>
            {children}
            {tip}
          </>
        ) : (
          <>
            {tip}
            {/* Swap loads after the mount, outside the boot (swap-boot.ts): until then the glyph is as it is. */}
            <LazySwap
              swapKey={swapKey}
              subjectKey={subjectKey}
              variant="icon"
              emphasis={emphasis ?? (props["aria-pressed"] === true || props["aria-pressed"] === "true" ? "pop" : undefined)}
              data-hot="quiet"
              fallback={children}
            >
              {children}
            </LazySwap>
          </>
        )}
      </Button>
    );
  },
);
IconButton.displayName = "IconButton";
